import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "/api";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const HEARTBEAT_MS = 15 * 1000;
const MAX_ACTIVE_SLICE_MS = 20 * 1000;

let lastPageview = { key: "", at: 0 };

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      ((window.crypto.getRandomValues(new Uint8Array(1))[0] & 15) >> (c / 4))
    ).toString(16)
  );
}

function trackingAllowed() {
  return !(
    navigator.doNotTrack === "1" ||
    window.doNotTrack === "1" ||
    navigator.globalPrivacyControl === true
  );
}

function getIdentity(forceNewSession = false) {
  let uid = localStorage.getItem("anon_uid");
  if (!uid) {
    uid = uuid();
    localStorage.setItem("anon_uid", uid);
  }

  const now = Date.now();
  const lastActivity = Number(sessionStorage.getItem("sid_ts") || 0);
  let sid = sessionStorage.getItem("sid");

  if (
    forceNewSession ||
    !sid ||
    !lastActivity ||
    now - lastActivity > SESSION_TIMEOUT_MS
  ) {
    sid = uuid();
  }

  sessionStorage.setItem("sid", sid);
  sessionStorage.setItem("sid_ts", String(now));
  return { uid, sid };
}

function sendJson(path, payload, preferBeacon = false) {
  const url = `${API}${path}`;
  const body = JSON.stringify(payload);

  if (preferBeacon && navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(url, blob)) return;
  }

  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

function recordPageview(identity, page) {
  const now = Date.now();
  const key = `${identity.sid}|${page}`;

  // React StrictMode menjalankan effect dua kali saat development.
  if (lastPageview.key === key && now - lastPageview.at < 1000) return;
  lastPageview = { key, at: now };

  sendJson(
    "/analytics/visit",
    {
      page,
      uid: identity.uid,
      sid: identity.sid,
      referrer: document.referrer || "",
    },
    true
  );
}

export default function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!trackingAllowed()) return undefined;

    const page = location.pathname || "/";
    let identity = getIdentity();
    let active =
      document.visibilityState === "visible" &&
      (typeof document.hasFocus !== "function" || document.hasFocus());
    let lastMark = Date.now();
    let pendingMs = 0;

    recordPageview(identity, page);

    const collectActiveTime = () => {
      const now = Date.now();
      if (active) {
        pendingMs += Math.min(now - lastMark, MAX_ACTIVE_SLICE_MS);
      }
      lastMark = now;
    };

    const flush = (preferBeacon = false) => {
      collectActiveTime();
      const seconds = Math.floor(pendingMs / 1000);
      if (seconds < 1) return;

      pendingMs -= seconds * 1000;
      sessionStorage.setItem("sid_ts", String(Date.now()));
      sendJson(
        "/analytics/engagement",
        { ...identity, page, seconds },
        preferBeacon
      );
    };

    const updateActivity = () => {
      collectActiveTime();
      const wasActive = active;
      active =
        document.visibilityState === "visible" &&
        (typeof document.hasFocus !== "function" || document.hasFocus());

      if (!active) {
        flush(true);
        return;
      }

      const lastActivity = Number(sessionStorage.getItem("sid_ts") || 0);
      if (!wasActive && Date.now() - lastActivity > SESSION_TIMEOUT_MS) {
        identity = getIdentity(true);
        recordPageview(identity, page);
      }
    };

    const intervalId = window.setInterval(() => flush(false), HEARTBEAT_MS);
    const handlePageHide = () => flush(true);

    document.addEventListener("visibilitychange", updateActivity);
    window.addEventListener("focus", updateActivity);
    window.addEventListener("blur", updateActivity);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.clearInterval(intervalId);
      flush(true);
      document.removeEventListener("visibilitychange", updateActivity);
      window.removeEventListener("focus", updateActivity);
      window.removeEventListener("blur", updateActivity);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [location.pathname]);

  return null;
}
